import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, UserRound } from 'lucide-react';
import type { ConversationStatus } from '@conversia/shared-types';
import * as conversationsApi from '@/api/conversations.api';
import { useToast } from '@/hooks/useToast';
import { isBotActive, botControlTarget, type BotControlMode } from '@/lib/bot-control';

/**
 * Control manual del bot por conversación. Reusa el endpoint de estado existente
 * (`PATCH /api/conversations/:id/status`): "Bot activo" → ACTIVE (el agente
 * retoma), "Atención humana" → WAITING_HUMAN (el agente NO responde aunque entren
 * mensajes — lo respeta el handler de entrada vía `RESPONDABLE_STATUSES`).
 *
 * El estado mostrado se DERIVA del `status` persistido de la conversación (prop),
 * no de estado local: por eso persiste al refrescar. Al guardar invalida la
 * conversación y la bandeja para que el toggle (y los badges) reflejen lo nuevo.
 */
export function BotControlToggle({
  conversationId,
  contactId,
  status,
}: {
  conversationId: string;
  contactId?: string;
  status?: ConversationStatus;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const active = isBotActive(status);

  const setMode = useMutation({
    mutationFn: (mode: BotControlMode) =>
      conversationsApi.updateStatus(conversationId, botControlTarget(mode)),
    onSuccess: (_res, mode) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      if (contactId) void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      toast.success(
        mode === 'bot'
          ? 'Bot reactivado en esta conversación'
          : 'Conversación en atención humana — el bot no responderá',
      );
    },
    onError: () => toast.error('No se pudo cambiar el control del bot'),
  });

  return (
    <BotControlToggleView
      active={active}
      pending={setMode.isPending}
      onSelect={(mode) => {
        // Idempotente: si ya estás en ese modo, no dispares una mutación inútil.
        if ((mode === 'bot') === active) return;
        setMode.mutate(mode);
      }}
    />
  );
}

/**
 * Vista del toggle (presentacional, sin fetch): segmented control de dos estados.
 * Separada del contenedor para poder renderizarla en tests sin QueryClient.
 */
export function BotControlToggleView({
  active,
  pending,
  onSelect,
}: {
  active: boolean;
  pending: boolean;
  onSelect: (mode: BotControlMode) => void;
}) {
  return (
    <section className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Control del bot</p>
      <div
        role="group"
        aria-label="Control del bot"
        className="grid grid-cols-2 gap-1 rounded-lg border border-surface-border p-1 dark:border-slate-700"
      >
        <button
          type="button"
          aria-pressed={active}
          disabled={pending}
          onClick={() => onSelect('bot')}
          className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
            active
              ? 'bg-brand-500 text-white shadow-sm'
              : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
          }`}
        >
          <Bot className="h-4 w-4" /> Bot activo
        </button>
        <button
          type="button"
          aria-pressed={!active}
          disabled={pending}
          onClick={() => onSelect('human')}
          className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
            !active
              ? 'bg-amber-500 text-white shadow-sm'
              : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
          }`}
        >
          <UserRound className="h-4 w-4" /> Atención humana
        </button>
      </div>
      <p className="text-[11px] text-slate-400">
        {active
          ? 'El agente responde automáticamente los mensajes entrantes.'
          : 'El agente NO responde; un humano lleva la conversación. Reactiválo cuando quieras.'}
      </p>
    </section>
  );
}
